import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addUser, editUser } from '../features/users/usersSlice';

export default function UserForm({ user, onCreated, onSaved, onCancel }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [status, setStatus] = useState('active');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorVerified, setTwoFactorVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPassword('');
      setStatus(user.status || 'active');
      setTwoFactorEnabled(user.twoFactorEnabled || false);
      setTwoFactorVerified(user.twoFactorVerified || false);
    } else {
      setName('');
      setEmail('');
      setPassword('');
      setRole('user');
      setStatus('active');
      setTwoFactorEnabled(false);
      setTwoFactorVerified(false);
    }
    setError(null);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (user) {
        const payload = { name, email, status, twoFactorEnabled, twoFactorVerified };
        if (password) payload.password = password;
        await dispatch(editUser({ id: user._id, payload })).unwrap();
        onSaved && onSaved();
      } else {
        await dispatch(addUser({ name, email, password, role })).unwrap();
        onCreated && onCreated();
        setName('');
        setEmail('');
        setPassword('');
        setRole('user');
        setStatus('active');
        setTwoFactorEnabled(false);
        setTwoFactorVerified(false);
      }
    } catch (err) {
      setError(err.message || JSON.stringify(err));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const primaryColor = '#05aa4be6';

  return (
    <form onSubmit={submit}>
      {error && (
        <div className="alert alert-danger d-flex align-items-center" style={{ borderRadius: '0.5rem' }}>
          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
          </svg>
          {error}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: '500' }}>Name</label>
        <input
          className="form-control"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={saving}
          required
          style={{ borderRadius: '0.5rem' }}
        />
      </div>

      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: '500' }}>Email</label>
        <input
          type="email"
          className="form-control"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={saving}
          required
          style={{ borderRadius: '0.5rem' }}
        />
      </div>

      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: '500' }}>
          Password {user ? '(leave blank to keep current)' : ''}
        </label>
        <input
          type="password"
          className="form-control"
          value={password}
          onChange={e => setPassword(e.target.value)}
          disabled={saving}
          minLength={6}
          { ...(user ? {} : { required: true }) }
          style={{ borderRadius: '0.5rem' }}
        />
        <div className="form-text">
          {user ? 'Only fill if you want to change the password' : 'Minimum 6 characters'}
        </div>
      </div>

      {!user && (
        <div className="mb-3">
          <label className="form-label" style={{ fontWeight: '500' }}>Role</label>
          <select
            className="form-select"
            value={role}
            onChange={e => setRole(e.target.value)}
            disabled={saving}
            required
            style={{ borderRadius: '0.5rem' }}
          >
            <option value="user">User</option>
            <option value="superadmin">Superadmin</option>
          </select>
          <div className="form-text">
            Superadmins have full access to manage users and customers
          </div>
        </div>
      )}

      {user && (
        <>
          <div className="mb-3">
            <label className="form-label" style={{ fontWeight: '500' }}>Status</label>
            <select
              className="form-select"
              value={status}
              onChange={e => setStatus(e.target.value)}
              disabled={saving}
              style={{ borderRadius: '0.5rem' }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="form-text">
              Inactive users cannot login
            </div>
          </div>

          <div className="mb-3">
            <div className="card" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '0.5rem' }}>
              <div className="card-body">
                <h6 className="card-title mb-3 d-flex align-items-center gap-2" style={{ color: primaryColor }}>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                  </svg>
                  Two-Factor Authentication
                </h6>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="twoFactorEnabled"
                    checked={twoFactorEnabled}
                    onChange={e => {
                      setTwoFactorEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setTwoFactorVerified(false);
                      }
                    }}
                    disabled={saving}
                    style={{ cursor: 'pointer' }}
                  />
                  <label className="form-check-label" htmlFor="twoFactorEnabled" style={{ cursor: 'pointer' }}>
                    Two-Factor Authentication Enabled
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="twoFactorVerified"
                    checked={twoFactorVerified}
                    onChange={e => setTwoFactorVerified(e.target.checked)}
                    disabled={saving || !twoFactorEnabled}
                    style={{ cursor: twoFactorEnabled ? 'pointer' : 'not-allowed' }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="twoFactorVerified"
                    style={{
                      cursor: twoFactorEnabled ? 'pointer' : 'not-allowed',
                      opacity: twoFactorEnabled ? 1 : 0.6
                    }}
                  >
                    Two-Factor Authentication Verified
                  </label>
                </div>

                <div className="alert alert-info d-flex align-items-start mt-3 mb-0" style={{ borderRadius: '0.5rem' }}>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" className="me-2 mt-1">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                  </svg>
                  <small>
                    <strong>Tip:</strong> Uncheck "Enabled" to completely remove 2FA from this user's account. This will clear their secret key and allow them to set up 2FA again from scratch.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="d-flex gap-2">
        <button
          className="btn text-white"
          disabled={saving}
          type="submit"
          style={{
            backgroundColor: primaryColor,
            border: 'none',
            borderRadius: '0.5rem'
          }}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Saving...
            </>
          ) : (
            user ? 'Update User' : 'Create User'
          )}
        </button>
        {user && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={saving}
            style={{ borderRadius: '0.5rem' }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
